import React from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  TextField,
  Grid,
  Alert,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  useTheme,
} from '@mui/material';
import StandardizedChip from '../../common/StandardizedChip';
import {
  Science as TestIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import { getDomainSpecificTestData } from './utils';

/**
 * PolicyTestingTab Component
 *
 * Displays policy testing interface with test input and results.
 *
 * @param {Object} props
 * @param {Object} props.policy - Policy object
 * @param {string} props.testContent - Test content value
 * @param {Function} props.onTestContentChange - Callback when test content changes
 * @param {Function} props.onTestPolicy - Callback to test the policy
 * @param {boolean} props.testing - Whether test is in progress
 * @param {Object} props.testResult - Test result object
 */
function PolicyTestingTab({
  policy,
  testContent,
  onTestContentChange,
  onTestPolicy,
  testing,
  testResult,
}) {
  const theme = useTheme();
  const domainName = policy?.name.replace('-policy', '');
  const testData = getDomainSpecificTestData(domainName);

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Policy Testing
      </Typography>
      <Box sx={{ mb: 3 }}>
        <Button
          variant="contained"
          startIcon={<TestIcon />}
          onClick={onTestPolicy}
          disabled={testing}
          sx={{ mb: 2 }}
        >
          {testing ? 'Testing...' : 'Test Policy'}
        </Button>

        {/* Test Data Information */}
        <Card variant="outlined" sx={{ mb: 2, p: 2 }}>
          <Typography variant="h6" gutterBottom>
            Test Data Information
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {testData.description}
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>Test Content:</strong>
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            value={testContent}
            onChange={e => onTestContentChange(e.target.value)}
            placeholder="Enter test content to validate against this policy..."
            variant="outlined"
            size="small"
            sx={{
              '& .MuiInputBase-input': {
                fontFamily: 'monospace',
                fontSize: '0.875rem',
              },
            }}
          />
          <Typography variant="body2" sx={{ mt: 2, mb: 1 }}>
            <strong>Expected Entities:</strong>
          </Typography>
          <Box display="flex" flexWrap="wrap" gap={0.5}>
            {testData.expectedEntities.map((entity, index) => (
              <StandardizedChip
                key={index}
                label={entity}
                color="primary"
                variant="outlined"
              />
            ))}
          </Box>
        </Card>
      </Box>

      {testResult && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Test Results
            </Typography>

            {/* Test Summary */}
            <Box sx={{ mb: 3 }}>
              <Grid container spacing={2}>
                <Grid size={{ xs: 6, sm: 3 }}>
                  <Card variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                    <Typography
                      variant="h4"
                      color={testResult.success ? 'success.main' : 'error.main'}
                    >
                      {testResult.total_violations || 0}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Violations
                    </Typography>
                  </Card>
                </Grid>
                <Grid size={{ xs: 6, sm: 3 }}>
                  <Card variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="h4" color="primary.main">
                      {testResult.compliance_rate || 0}%
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Compliance
                    </Typography>
                  </Card>
                </Grid>
                <Grid size={{ xs: 6, sm: 3 }}>
                  <Card variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="h4" color="warning.main">
                      {testResult.risk_score || 0}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Risk Score
                    </Typography>
                  </Card>
                </Grid>
                <Grid size={{ xs: 6, sm: 3 }}>
                  <Card variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="h4" color="info.main">
                      {testResult.processing_time
                        ? (testResult.processing_time * 1000).toFixed(0)
                        : 0}
                      ms
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Processing Time
                    </Typography>
                  </Card>
                </Grid>
              </Grid>
            </Box>

            {/* Success/Error Alert */}
            {testResult.success ? (
              <Alert severity="success" icon={<CheckIcon />} sx={{ mb: 2 }}>
                Content check completed successfully
                {testResult.total_violations > 0 && (
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Found {testResult.total_violations} violations with{' '}
                    {testResult.compliance_rate}% compliance rate
                  </Typography>
                )}
              </Alert>
            ) : (
              <Alert severity="error" icon={<ErrorIcon />} sx={{ mb: 2 }}>
                Content check failed:{' '}
                {testResult.error_message || 'Unknown error'}
              </Alert>
            )}

            {/* Violations Details */}
            {testResult.violations && testResult.violations.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Detected Violations ({testResult.violations.length})
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Type</TableCell>
                        <TableCell>Severity</TableCell>
                        <TableCell>Description</TableCell>
                        <TableCell>Confidence</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {testResult.violations
                        .slice(0, 5)
                        .map((violation, index) => (
                          <TableRow key={index}>
                            <TableCell>
                              <StandardizedChip
                                label={violation.violation_type}
                                color={
                                  violation.severity === 'critical'
                                    ? 'error'
                                    : 'warning'
                                }
                              />
                            </TableCell>
                            <TableCell>
                              <StandardizedChip
                                label={violation.severity}
                                variant="outlined"
                                color={
                                  violation.severity === 'critical'
                                    ? 'error'
                                    : 'warning'
                                }
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant="caption">
                                {violation.description}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="caption">
                                {violation.confidence
                                  ? (violation.confidence * 100).toFixed(0) +
                                    '%'
                                  : 'N/A'}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                {testResult.violations.length > 5 && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mt: 1, display: 'block' }}
                  >
                    ... and {testResult.violations.length - 5} more violations
                  </Typography>
                )}
              </Box>
            )}

            {/* Anonymization Results */}
            {testResult.anonymization_results &&
              testResult.anonymization_results.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Anonymization Results (
                    {testResult.anonymization_results.length})
                  </Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Entity</TableCell>
                          <TableCell>Method</TableCell>
                          <TableCell>Original</TableCell>
                          <TableCell>Anonymized</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {testResult.anonymization_results.map(
                          (result, index) => (
                            <TableRow key={index}>
                              <TableCell>
                                <StandardizedChip
                                  label={result.entity_type}
                                  color="primary"
                                />
                              </TableCell>
                              <TableCell>
                                <StandardizedChip
                                  label={result.anonymization_method}
                                  variant="outlined"
                                />
                              </TableCell>
                              <TableCell>
                                <Typography
                                  variant="caption"
                                  sx={{ fontFamily: 'monospace' }}
                                >
                                  {result.original_text}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography
                                  variant="caption"
                                  sx={{ fontFamily: 'monospace' }}
                                >
                                  {result.anonymized_text}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          )
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}

            {/* Filtered Content */}
            {testResult.filtered_content && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Anonymized Content
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    fontFamily: 'monospace',
                    backgroundColor: theme.palette.background.default,
                    p: 2,
                    display: 'block',
                    borderRadius: 1,
                    whiteSpace: 'pre-wrap',
                    fontSize: '0.8rem',
                  }}
                >
                  {JSON.stringify(testResult.filtered_content, null, 2)}
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      )}
    </Box>
  );
}

export default PolicyTestingTab;
